<template>
  <div class="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-blue-100">
    <div class="flex items-center mb-4">
      <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center mr-3">
        <span class="material-symbols-outlined text-white text-2xl">search</span>
      </div>
      <div>
        <h2 class="text-xl font-bold text-gray-900 text-left">Plan Your Trip</h2>
        <p class="text-sm text-gray-600">Find the best route for your journey</p>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Origin Selector -->
      <div class="relative">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          <span class="flex items-center">
            <span class="material-symbols-outlined text-green-600 text-lg mr-1">trip_origin</span>
            From (Origin)
          </span>
        </label>
        <div class="relative">
          <select
            v-model="selectedOrigin"
            @change="handleOriginChange"
            class="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all appearance-none bg-white cursor-pointer hover:border-blue-300"
          >
            <option value="">Select pickup location</option>
            <option v-for="location in originLocations" :key="location" :value="location">
              {{ location }}
            </option>
          </select>
          <span class="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            expand_more
          </span>
        </div>
      </div>

      <!-- Swap Button (Middle) -->
      <div class="flex items-end justify-center pb-3">
        <button
          @click="swapLocations"
          :disabled="!selectedOrigin && !selectedDestination"
          class="p-3 rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-110 active:scale-95"
          title="Swap origin and destination"
        >
          <span class="material-symbols-outlined text-2xl">swap_horiz</span>
        </button>
      </div>

      <!-- Destination Selector -->
      <div class="relative">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          <span class="flex items-center">
            <span class="material-symbols-outlined text-red-600 text-lg mr-1">location_on</span>
            To (Destination)
          </span>
        </label>
        <div class="relative">
          <select
            v-model="selectedDestination"
            @change="handleDestinationChange"
            class="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all appearance-none bg-white cursor-pointer hover:border-blue-300"
          >
            <option value="">Select drop-off location</option>
            <option v-for="location in destinationLocations" :key="location" :value="location">
              {{ location }}
            </option>
          </select>
          <span class="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            expand_more
          </span>
        </div>
      </div>
    </div>

    <!-- Search Results Summary -->
    <div v-if="selectedOrigin || selectedDestination" class="mt-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
      <div class="flex items-start">
        <span class="material-symbols-outlined text-blue-700 mr-2">info</span>
        <div class="flex-1">
          <p class="text-sm font-semibold text-blue-900 mb-1">Trip Summary</p>
          <div class="flex items-center text-sm text-blue-800">
            <span v-if="selectedOrigin" class="font-medium">{{ selectedOrigin }}</span>
            <span v-else class="text-blue-600 italic">Any location</span>
            
            <span class="material-symbols-outlined mx-2 text-lg">arrow_forward</span>
            
            <span v-if="selectedDestination" class="font-medium">{{ selectedDestination }}</span>
            <span v-else class="text-blue-600 italic">Any location</span>
          </div>
          <p v-if="matchingRoutesCount !== null" class="text-xs text-blue-700 mt-1">
            {{ matchingRoutesCount }} {{ matchingRoutesCount === 1 ? 'route' : 'routes' }} found
          </p>
        </div>
        <button
          @click="clearSearch"
          class="ml-2 p-1 rounded-full hover:bg-blue-200 transition-colors"
          title="Clear search"
        >
          <span class="material-symbols-outlined text-blue-700 text-lg">close</span>
        </button>
      </div>
    </div>

    <!-- No Results Message -->
    <div v-if="selectedOrigin && selectedDestination && matchingRoutesCount === 0" class="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
      <div class="flex items-start">
        <span class="material-symbols-outlined text-yellow-600 mr-2">warning</span>
        <div>
          <p class="text-sm font-semibold text-yellow-900 mb-1">No Direct Routes Found</p>
          <p class="text-xs text-yellow-800">
            There are no direct bus routes from <strong>{{ selectedOrigin }}</strong> to <strong>{{ selectedDestination }}</strong>. 
            Try selecting different locations or check individual routes below.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BusTripSearch',
  props: {
    routes: {
      type: Array,
      required: true
    },
    matchingRoutesCount: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      selectedOrigin: '',
      selectedDestination: ''
    };
  },
  computed: {
    // Get all unique pickup locations from all routes
    originLocations() {
      const locations = new Set();
      this.routes.forEach(route => {
        if (route.pickup_locations && Array.isArray(route.pickup_locations)) {
          route.pickup_locations.forEach(loc => locations.add(loc));
        }
      });
      return Array.from(locations).sort();
    },
    
    // Get all unique drop-off locations from all routes
    destinationLocations() {
      const locations = new Set();
      this.routes.forEach(route => {
        if (route.dropoff_locations && Array.isArray(route.dropoff_locations)) {
          route.dropoff_locations.forEach(loc => locations.add(loc));
        }
      });
      return Array.from(locations).sort();
    }
  },
  methods: {
    handleOriginChange() {
      this.emitSearch();
    },
    
    handleDestinationChange() {
      this.emitSearch();
    },
    
    swapLocations() {
      const temp = this.selectedOrigin;
      this.selectedOrigin = this.selectedDestination;
      this.selectedDestination = temp;
      this.emitSearch();
    },
    
    clearSearch() {
      this.selectedOrigin = '';
      this.selectedDestination = '';
      this.emitSearch();
    },
    
    emitSearch() {
      this.$emit('search', {
        origin: this.selectedOrigin,
        destination: this.selectedDestination
      });
    }
  }
};
</script>

<style scoped>
/* Custom select styling */
select {
  background-image: none;
}

/* Smooth transitions */
.transition-all {
  transition: all 0.2s ease-in-out;
}

/* Remove default select arrow in IE */
select::-ms-expand {
  display: none;
}
</style>
